import { useState } from "react";
import {
  Alert, Box, Button, Checkbox, FormControlLabel, IconButton, Paper, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { wardIssuesApi, type CreateWardIssueLineDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";

/**
 * UC-FN-HOS-01 — Create ward issue. Minimal form (single screen, no picker shell)
 * — the picker integration lands when the universal picker reshell ships.
 */
export function CreateWardIssuePage() {
  const navigate = useNavigate();
  const [uhid, setUhid] = useState("");
  const [admissionId, setAdmissionId] = useState("");
  const [wardCode, setWardCode] = useState("");
  const [wardWarehouseId, setWardWarehouseId] = useState("");
  const [isEmergency, setIsEmergency] = useState(false);
  const [reason, setReason] = useState("");
  const [lines, setLines] = useState<CreateWardIssueLineDto[]>([
    { itemId: "", batchId: "", qty: 1, lineNotes: "" },
  ]);

  const mutation = useMutation({
    meta: { successMessage: "Ward issue created" },
    mutationFn: () => wardIssuesApi.create({
      uhid, admissionId: admissionId || undefined, wardCode: wardCode || undefined,
      wardWarehouseId, isEmergency, reason: reason || undefined,
      lines: lines.filter(l => l.itemId && l.batchId && l.qty > 0),
    }),
    onSuccess: () => navigate("/hospital/ward"),
  });

  const update = (i: number, patch: Partial<CreateWardIssueLineDto>) =>
    setLines(prev => prev.map((l, idx) => idx === i ? { ...l, ...patch } : l));

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>New ward issue</Typography>
      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField label="UHID" value={uhid} required size="small" sx={{ flex: 1 }}
              onChange={(e) => setUhid(e.target.value)} />
            <TextField label="Admission ID" value={admissionId} size="small" sx={{ flex: 1 }}
              onChange={(e) => setAdmissionId(e.target.value)} />
            <TextField label="Ward" value={wardCode} size="small" sx={{ flex: 1 }}
              onChange={(e) => setWardCode(e.target.value)} />
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField label="Ward warehouse ID" value={wardWarehouseId} required size="small" sx={{ flex: 1 }}
              onChange={(e) => setWardWarehouseId(e.target.value)} />
            <FormControlLabel control={
              <Checkbox checked={isEmergency} onChange={(e) => setIsEmergency(e.target.checked)} />
            } label="Emergency" />
          </Stack>
          <TextField label="Reason" value={reason} size="small" multiline minRows={2}
            onChange={(e) => setReason(e.target.value)} />

          <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>Lines</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Item ID</TableCell><TableCell>Batch ID</TableCell>
                <TableCell width={100}>Qty</TableCell><TableCell>Notes</TableCell>
                <TableCell width={40} />
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((l, i) => (
                <TableRow key={i}>
                  <TableCell><TextField size="small" fullWidth value={l.itemId}
                    onChange={(e) => update(i, { itemId: e.target.value })} /></TableCell>
                  <TableCell><TextField size="small" fullWidth value={l.batchId}
                    onChange={(e) => update(i, { batchId: e.target.value })} /></TableCell>
                  <TableCell><TextField size="small" type="number" value={l.qty}
                    onChange={(e) => update(i, { qty: Number(e.target.value) })} /></TableCell>
                  <TableCell><TextField size="small" fullWidth value={l.lineNotes ?? ""}
                    onChange={(e) => update(i, { lineNotes: e.target.value })} /></TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() =>
                      setLines(prev => prev.filter((_, idx) => idx !== i))}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Button startIcon={<AddIcon />} size="small"
            onClick={() => setLines(prev => [...prev, { itemId: "", batchId: "", qty: 1, lineNotes: "" }])}>
            Add line
          </Button>

          {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          {(!uhid || !wardWarehouseId || !lines.some(l => l.itemId && l.batchId && l.qty > 0)) && (
            <Alert severity="info" sx={{ mt: 1 }}>
              {!uhid ? "UHID is required."
                : !wardWarehouseId ? "Pick a ward warehouse."
                : "Add at least one line with an item, batch and quantity."}
            </Alert>
          )}
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={() => navigate("/hospital/ward")}>Cancel</Button>
            <Button variant="contained" onClick={() => mutation.mutate()}
              disabled={!uhid || !wardWarehouseId ||
                !lines.some(l => l.itemId && l.batchId && l.qty > 0) || mutation.isPending}>
              Save draft
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
